/**
 * Malcriados · carrito (panel lateral y pagina /cart).
 *
 * El HTML lo pinta Liquid (snippets/mc-carrito). Este modulo solo agrega la interaccion:
 *  - cantidad, quitar, mejoras, caja de regalo y agregar productos con la API Ajax del carrito;
 *  - tras cada cambio emite el evento estandar de Horizon (CartLinesUpdateEvent) con el HTML de
 *    las secciones del carrito: <cart-items-component> repinta el panel y el contador del
 *    encabezado se actualiza solo, igual que cuando Horizon agrega un producto;
 *  - la hoja para elegir prenda, color y talla, y la ventana de la caja de regalo.
 *
 * Todo cuelga de `document` (delegacion), asi sigue funcionando cuando el carrito se vuelve a
 * pintar o cuando el editor de temas recarga la seccion.
 *
 * Contrato de datos con la pagina de producto (propiedades de linea):
 *   _diseno     identifica el diseño (prendas que comparten foto)
 *   _prenda     identifica la prenda: <_diseno>-<n>
 *   _mejora_de  en la linea de una mejora: el _prenda al que pertenece
 */

/** @type {any} */
let Eventos = null;
try {
  Eventos = await import('@shopify/events');
} catch {
  Eventos = null;
}

const ERROR_GENERICO = 'No pudimos actualizar tu carrito. Intenta de nuevo.';
const UNIVERSALES = ['*', 'todos', 'todas'];

let ocupado = false;

/* ------------------------------------------------------------
 * Utilidades
 * ------------------------------------------------------------ */

/** @param {number} centavos */
function dinero(centavos) {
  const mc = /** @type {any} */ (window).MC;
  if (mc && typeof mc.dinero === 'function') return mc.dinero(centavos);
  return '$' + String(Math.round(centavos / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** @param {unknown} texto */
function limpio(texto) {
  return String(texto ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function rutas() {
  const r = /** @type {any} */ (window).Theme?.routes ?? {};
  const carrito = r.cart_url || '/cart';
  return {
    agregar: r.cart_add_url || '/cart/add.js',
    actualizar: (r.cart_update_url || '/cart/update') + '.js',
    cambiar: (r.cart_change_url || '/cart/change') + '.js',
    carrito: carrito + '.js',
  };
}

function idsDeSecciones() {
  const ids = new Set();
  for (const nodo of document.querySelectorAll('cart-items-component')) {
    if (nodo instanceof HTMLElement && nodo.dataset.sectionId) ids.add(nodo.dataset.sectionId);
  }
  return Array.from(ids);
}

/** @param {boolean} valor */
function marcarOcupado(valor) {
  for (const raiz of document.querySelectorAll('[data-mc-carrito]')) {
    raiz.classList.toggle('mc-carrito--ocupado', valor);
    raiz.setAttribute('aria-busy', valor ? 'true' : 'false');
  }
}

/** @param {string} mensaje */
function avisar(mensaje) {
  for (const nodo of document.querySelectorAll('[data-mc-carrito-aviso]')) {
    if (!(nodo instanceof HTMLElement)) continue;
    nodo.textContent = mensaje;
    nodo.hidden = !mensaje;
  }
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} cuerpo
 * @param {boolean} [conSecciones] pedir tambien el HTML del carrito (solo en la ultima llamada de una accion)
 */
async function pedir(url, cuerpo, conSecciones = true) {
  const respuesta = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(
      conSecciones ? { ...cuerpo, sections: idsDeSecciones().join(','), sections_url: window.location.pathname } : cuerpo
    ),
  });
  /** @type {any} */
  let datos = null;
  try {
    datos = await respuesta.json();
  } catch {
    datos = null;
  }
  if (!respuesta.ok || !datos || datos.errors) {
    const detalle = datos && (datos.description || datos.message || (typeof datos.errors === 'string' ? datos.errors : ''));
    throw new Error(detalle || ERROR_GENERICO);
  }
  return datos;
}

async function leerCarrito() {
  const respuesta = await fetch(rutas().carrito, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!respuesta.ok) throw new Error(ERROR_GENERICO);
  return respuesta.json();
}

/**
 * Repinta a mano si el modulo de eventos de Horizon no cargo.
 * @param {Record<string, string> | undefined} secciones
 */
function pintarAMano(secciones) {
  if (!secciones) {
    window.location.reload();
    return;
  }
  for (const [id, html] of Object.entries(secciones)) {
    if (!html) continue;
    const nuevo = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-items-component');
    const actual = document.querySelector(`cart-items-component[data-section-id="${CSS.escape(id)}"]`);
    if (nuevo && actual) actual.innerHTML = nuevo.innerHTML;
  }
  rellenarLlegada();
}

/**
 * Ejecuta un cambio del carrito y avisa a Horizon con su evento estandar.
 *
 * @param {'add' | 'update' | 'remove'} accion
 * @param {Array<Record<string, unknown>>} lineas
 * @param {() => Promise<{ carrito: any, secciones: Record<string, string> | undefined }>} ejecutar
 * @returns {Promise<boolean>} true si el cambio se hizo.
 */
async function operar(accion, lineas, ejecutar) {
  if (ocupado) return false;
  ocupado = true;
  marcarOcupado(true);
  avisar('');

  const Evento = Eventos?.CartLinesUpdateEvent;
  const diferido = Evento?.createPromise?.() ?? null;

  if (diferido) {
    /* Quien escucha el evento maneja el rechazo; esto evita el aviso de promesa sin manejar. */
    diferido.promise.catch(() => {});
    document.dispatchEvent(new Evento({ action: accion, context: 'cart', lines: lineas, promise: diferido.promise }));
  }

  try {
    const { carrito, secciones } = await ejecutar();
    if (diferido) {
      diferido.resolve({
        cart: Evento.createCartFromAjaxResponse(carrito),
        detail: {
          sections: secciones,
          items: carrito.items,
          itemCount: carrito.item_count,
          source: 'mc-carrito',
          didError: false,
        },
      });
    } else {
      pintarAMano(secciones);
    }
    return true;
  } catch (error) {
    const mensaje = error instanceof Error && error.message ? error.message : ERROR_GENERICO;
    diferido?.reject(error instanceof Error ? error : new Error(mensaje));
    avisar(mensaje);
    return false;
  } finally {
    ocupado = false;
    marcarOcupado(false);
  }
}

/* ------------------------------------------------------------
 * Propiedades del sistema de packs
 * ------------------------------------------------------------
 * La app de descuentos (malcriados-discounts) y el flujo de pedidos leen estas propiedades:
 *   __mc_pedido_id / __mc_pack_id   agrupan las lineas de un mismo diseno
 *   __mc_pack_pos / __mc_pack_total posicion de la prenda y cuantas hay
 *   _mc_tier_group / _mc_tier_qty   descuento por volumen (solo con 2 o 3 prendas)
 *   __mc_extra_for_pos, __mc_tipo, Para prenda   un extra (mejora, jockey) ligado a su prenda
 * El carrito las deriva de _diseno / _prenda / _mejora_de cada vez que algo cambia, para que
 * agregar o quitar una prenda deje el descuento y las posiciones bien.
 */

/** @param {any} item */
function propiedadesDe(item) {
  return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
}

/** @param {string} prenda @returns {number} */
function numeroDePrenda(prenda) {
  const n = Number(String(prenda).split('-').pop());
  return Number.isFinite(n) ? n : 0;
}

/**
 * Cambios de propiedades que deja el carrito consistente.
 * @param {any} carrito
 * @returns {Array<{ key: string, quantity: number, properties: Record<string, string> }>}
 */
function cambiosDePacks(carrito) {
  const items = Array.isArray(carrito?.items) ? carrito.items : [];
  /** @type {Map<string, any[]>} */
  const disenos = new Map();
  for (const item of items) {
    const d = propiedadesDe(item)._diseno;
    if (!d) continue;
    if (!disenos.has(d)) disenos.set(d, []);
    disenos.get(d)?.push(item);
  }

  /** @type {Array<{ key: string, quantity: number, properties: Record<string, string> }>} */
  const cambios = [];
  for (const [diseno, lineas] of disenos) {
    const prendas = lineas
      .filter((item) => propiedadesDe(item)._prenda && !propiedadesDe(item)._mejora_de)
      .sort((a, b) => numeroDePrenda(propiedadesDe(a)._prenda) - numeroDePrenda(propiedadesDe(b)._prenda));
    const total = prendas.reduce((suma, item) => suma + item.quantity, 0);
    /** @type {Map<string, number>} */
    const posiciones = new Map();
    prendas.forEach((item, i) => posiciones.set(propiedadesDe(item)._prenda, i + 1));

    for (const item of lineas) {
      const actual = propiedadesDe(item);
      /** @type {Record<string, string>} */
      const deseado = { __mc_pedido_id: diseno, __mc_pack_id: diseno };
      if (actual._prenda && !actual._mejora_de) {
        deseado.__mc_pack_total = String(prendas.length);
        deseado.__mc_pack_pos = String(posiciones.get(actual._prenda) ?? 1);
        const conDescuento = total >= 2 && total <= 6 && prendas.length === total;
        deseado._mc_tier_group = conDescuento ? diseno : '';
        deseado._mc_tier_qty = conDescuento ? String(total) : '';
      } else if (actual._mejora_de) {
        const posicion = posiciones.get(actual._mejora_de);
        if (!posicion) continue;
        deseado.__mc_extra_for_pos = String(posicion);
        deseado.__mc_tipo = 'extra';
        deseado['Para prenda'] = String(posicion);
      } else {
        continue;
      }

      const diferente = Object.entries(deseado).some(([clave, valor]) => (actual[clave] ?? '') !== valor);
      if (!diferente) continue;
      cambios.push({ key: item.key, quantity: item.quantity, properties: { ...actual, ...deseado } });
    }
  }
  return cambios;
}

/* ------------------------------------------------------------
 * Carrito en memoria y cambios con el minimo de llamadas
 * ------------------------------------------------------------
 * Antes cada accion repintaba el carrito varias veces (una por cada propiedad que habia que
 * corregir) y releia el carrito entre medias. Ahora:
 *   - se guarda una copia del carrito y se simula el cambio para saber, antes de llamar, que
 *     lineas necesitan propiedades nuevas;
 *   - las propiedades de la linea nueva viajan en la misma llamada que la agrega;
 *   - solo la ultima llamada de la accion pide el HTML del carrito.
 */

/** @type {any} */
let copia = null;

async function carritoActual() {
  if (!copia) copia = await leerCarrito();
  return copia;
}

/** @param {any} carrito */
function clonarItems(carrito) {
  return (Array.isArray(carrito?.items) ? carrito.items : []).map((/** @type {any} */ item) => ({
    ...item,
    properties: { ...propiedadesDe(item) },
  }));
}

/**
 * Posicion (1, 2, 3...) de cada linea a corregir dentro del carrito tal como quedara.
 * Las claves de linea NO sirven aqui: Shopify las cambia en todas las lineas al agregar o quitar.
 * La posicion si es estable: lo nuevo entra primero y cambiar propiedades no mueve la linea.
 * @param {Array<{ key: string, quantity: number, properties: Record<string, string> }>} ajustes
 * @param {Array<{ key: string }>} orden lineas del carrito simulado, en su orden
 */
function conPosicion(ajustes, orden) {
  return ajustes.map((ajuste) => ({ ...ajuste, linea: orden.findIndex((item) => item.key === ajuste.key) + 1 }));
}

/**
 * Aplica los ajustes de propiedades uno tras otro; solo el ultimo pide el HTML.
 * @param {Array<{ linea: number, quantity: number, properties: Record<string, string> }>} ajustes
 * @returns {Promise<{ carrito: any, secciones: Record<string, string> | undefined }>}
 */
async function aplicarAjustes(ajustes) {
  /** @type {any} */
  let ultimo = null;
  for (const [i, ajuste] of ajustes.entries()) {
    ultimo = await pedir(
      rutas().cambiar,
      { line: ajuste.linea, quantity: ajuste.quantity, properties: ajuste.properties },
      i === ajustes.length - 1
    );
  }
  copia = ultimo;
  return { carrito: ultimo, secciones: ultimo?.sections };
}

/**
 * Cuando hay que corregir varias lineas, en vez de una llamada por linea se hace en dos:
 *   1) agregar de una vez las lineas con sus propiedades nuevas (mas la linea nueva, si la hay);
 *   2) una sola actualizacion de cantidades que deja en 0 las versiones viejas (y lo que se quita).
 * Si Shopify rechaza la primera (por ejemplo por stock, al haber las dos versiones a la vez),
 * no cambia nada y devuelve null para usar el camino de una linea por llamada.
 * @param {any} base carrito antes del cambio
 * @param {Record<string, number>} cambios cantidades pedidas por clave de linea
 * @param {Array<{ key: string, quantity: number, properties: Record<string, string> }>} ajustes
 * @param {Record<string, unknown> | null} nuevaLinea linea que se agrega, si es un "agregar"
 * @returns {Promise<{ carrito: any, secciones: Record<string, string> | undefined } | null>}
 */
async function aplicarEnLote(base, cambios, ajustes, nuevaLinea) {
  const claves = new Set(ajustes.map((ajuste) => ajuste.key));
  /** @type {Array<Record<string, unknown>>} */
  const reanadir = [];
  for (const ajuste of ajustes) {
    if (ajuste.key === '__nueva__') continue;
    const vieja = base.items.find((/** @type {any} */ item) => item.key === ajuste.key);
    if (!vieja) return null;
    reanadir.push({ id: Number(vieja.variant_id || vieja.id), quantity: ajuste.quantity, properties: ajuste.properties });
  }
  const nuevas = nuevaLinea ? [nuevaLinea, ...reanadir] : reanadir;
  try {
    await pedir(rutas().agregar, { items: nuevas }, false);
  } catch {
    return null;
  }
  /* Lo agregado queda al principio; despues siguen las lineas de antes, en su orden. */
  const cantidades = [
    ...nuevas.map((linea) => Number(linea.quantity) || 1),
    ...base.items.map((/** @type {any} */ item) =>
      claves.has(item.key) || cambios[item.key] === 0 ? 0 : (cambios[item.key] ?? item.quantity)
    ),
  ];
  const carrito = await pedir(rutas().actualizar, { updates: cantidades });
  copia = carrito;
  return { carrito, secciones: carrito.sections };
}

/** @param {Record<string, number>} cambios  clave de linea -> cantidad */
function actualizar(cambios) {
  const lineas = Object.entries(cambios).map(([id, quantity]) => ({ id, quantity }));
  const quita = lineas.every((linea) => linea.quantity === 0);
  return operar(quita ? 'remove' : 'update', lineas, async () => {
    let base = await carritoActual();
    /* Si la copia quedo vieja respecto de lo que muestra la pantalla, se vuelve a leer. */
    if (Object.keys(cambios).some((clave) => !base.items.some((/** @type {any} */ item) => item.key === clave))) {
      base = copia = await leerCarrito();
    }
    const simulado = {
      items: clonarItems(base)
        .map((/** @type {any} */ item) => ({ ...item, quantity: cambios[item.key] ?? item.quantity }))
        .filter((/** @type {any} */ item) => item.quantity > 0),
    };
    const ajustes = cambiosDePacks(simulado);
    if (!ajustes.length) {
      const carrito = await pedir(rutas().actualizar, { updates: cambios });
      copia = carrito;
      return { carrito, secciones: carrito.sections };
    }
    if (ajustes.length >= 2) {
      const enLote = await aplicarEnLote(base, cambios, ajustes, null);
      if (enLote) return enLote;
    }
    await pedir(rutas().actualizar, { updates: cambios }, false);
    return aplicarAjustes(conPosicion(ajustes, simulado.items));
  });
}

/**
 * @param {string | number} variante
 * @param {Record<string, string>} [propiedades]
 */
function agregar(variante, propiedades) {
  /** @type {Record<string, unknown>} */
  const item = { id: Number(variante), quantity: 1 };
  if (propiedades && Object.keys(propiedades).length > 0) item.properties = propiedades;
  return operar('add', [{ merchandiseId: String(variante), quantity: 1 }], async () => {
    /* Un extra se liga a su prenda mirando el carrito real: la copia puede estar vieja si la prenda
       se agrego desde la ficha de producto, y sin la prenda el extra queda sin sus propiedades
       (y pierde el descuento). */
    if (propiedades && propiedades._mejora_de) copia = null;
    const base = await carritoActual();
    const clave = '__nueva__';
    /* Lo nuevo entra al principio de la lista. */
    const simulado = {
      items: [{ key: clave, quantity: 1, properties: { ...(propiedades || {}) } }, ...clonarItems(base)],
    };
    const ajustes = cambiosDePacks(simulado);
    const propio = ajustes.find((ajuste) => ajuste.key === clave);
    const otros = ajustes.filter((ajuste) => ajuste.key !== clave);
    if (propio) item.properties = propio.properties;

    if (otros.length >= 2) {
      const enLote = await aplicarEnLote(base, {}, otros, item);
      if (enLote) return enLote;
    }
    const respuesta = await pedir(rutas().agregar, { items: [item] }, otros.length === 0);
    if (otros.length) return aplicarAjustes(conPosicion(otros, simulado.items));

    /* Sin releer el carrito: se arma uno aproximado con lo agregado (suficiente para el contador y el
       panel, que se pintan con el HTML de la respuesta) y se corrige solo, en segundo plano. */
    const nuevos = Array.isArray(respuesta.items) ? respuesta.items : [respuesta];
    const aproximado = {
      ...base,
      items: [...nuevos, ...clonarItems(base)],
      item_count: (base.item_count || 0) + nuevos.reduce((suma, /** @type {any} */ n) => suma + (n.quantity || 1), 0),
      total_price: (base.total_price || 0) + nuevos.reduce((suma, /** @type {any} */ n) => suma + (n.final_line_price || n.price || 0), 0),
    };
    copia = aproximado;
    leerCarrito()
      .then((real) => {
        if (copia === aproximado) copia = real;
      })
      .catch(() => {
        copia = null;
      });
    return { carrito: aproximado, secciones: respuesta.sections };
  });
}

/* ------------------------------------------------------------
 * Fecha de llegada: al repintar, el texto vuelve vacio del servidor;
 * mc-base lo rellena cuando recibe "mc:contenido".
 * ------------------------------------------------------------ */

let llegadaPendiente = false;

function rellenarLlegada() {
  if (llegadaPendiente) return;
  llegadaPendiente = true;
  requestAnimationFrame(() => {
    llegadaPendiente = false;
    if (document.querySelector('[data-mc-carrito] [data-mc-llegada]:empty')) {
      document.dispatchEvent(new CustomEvent('mc:contenido'));
    }
  });
}

const vigilados = new WeakSet();
const vigia = new MutationObserver(rellenarLlegada);

/** Deja lista la copia del carrito para que la primera accion no tenga que esperar a leerlo. */
function precargarCopia() {
  if (copia) return;
  const leer = () => {
    carritoActual().catch(() => {});
    /* Las prendas para "agregar otra prenda con este diseño" tambien se piden antes de que se necesiten. */
    const raiz = document.querySelector('[data-mc-carrito][data-url-prendas]');
    if (raiz instanceof HTMLElement && raiz.dataset.urlPrendas && raiz.querySelector('[data-mc-otra]')) {
      pedirPrendas(raiz.dataset.urlPrendas).catch(() => {});
    }
  };
  if ('requestIdleCallback' in window) /** @type {any} */ (window).requestIdleCallback(leer, { timeout: 2000 });
  else setTimeout(leer, 600);
}

function vigilar() {
  for (const nodo of document.querySelectorAll('cart-items-component')) {
    if (vigilados.has(nodo) || !nodo.querySelector('[data-mc-carrito]')) continue;
    vigilados.add(nodo);
    vigia.observe(nodo, { childList: true, subtree: true });
    precargarCopia();
  }
  rellenarLlegada();
}

vigilar();
if (document.querySelector('[data-mc-carrito]')) precargarCopia();
document.addEventListener('shopify:section:load', vigilar);
document.addEventListener('cart-section:restored', rellenarLlegada);
if (Eventos?.StandardEvents?.cartLinesUpdate) {
  document.addEventListener(Eventos.StandardEvents.cartLinesUpdate, (evento) => {
    /* Si el cambio lo hizo otra parte (ficha de producto, etc.), la copia del carrito ya no vale. */
    const contexto = /** @type {any} */ (evento).detail?.context;
    if (contexto && contexto !== 'cart') copia = null;
    vigilar();
    /** @type {any} */ (evento).promise?.then(() => setTimeout(rellenarLlegada, 60)).catch(() => {});
  });
}

/* ------------------------------------------------------------
 * Capas: hoja de eleccion y ventana de la caja
 * ------------------------------------------------------------ */

/** @type {HTMLElement | null} */
let quienAbrio = null;

/** @param {string} nombre */
function capa(nombre) {
  const nodo = document.querySelector(`[data-mc-carrito-capa="${nombre}"]`);
  return nodo instanceof HTMLElement ? nodo : null;
}

function capaAbierta() {
  const nodo = document.querySelector('[data-mc-carrito-capa]:not([hidden])');
  return nodo instanceof HTMLElement ? nodo : null;
}

/**
 * @param {HTMLElement} nodo
 * @param {Element | null} origen
 */
function abrirCapa(nodo, origen) {
  quienAbrio = origen instanceof HTMLElement ? origen : null;
  nodo.hidden = false;
  const foco = nodo.querySelector('[data-mc-capa-cerrar]');
  if (foco instanceof HTMLElement) foco.focus();
}

function cerrarCapas() {
  for (const nodo of document.querySelectorAll('[data-mc-carrito-capa]')) {
    if (nodo instanceof HTMLElement) nodo.hidden = true;
  }
  hoja = null;
  if (quienAbrio && document.contains(quienAbrio)) quienAbrio.focus();
  quienAbrio = null;
}

/* ------------------------------------------------------------
 * Hoja de eleccion (prenda, color, talla)
 * ------------------------------------------------------------ */

/**
 * @typedef {[number, number, number, string[]]} Variante  id, precio, disponible, valores
 * @typedef {{ id: number, t: string, e: string, b: string, i?: string, c?: Record<string, string>, o: string[], v: Variante[] }} Producto
 * @typedef {{
 *   modo: 'diseno' | 'aparte' | 'mejora',
 *   productos: Producto[],
 *   indice: number,
 *   eleccion: string[],
 *   titulo: string,
 *   estilo: string,
 *   retratos: number | null,
 *   pct: number,
 *   diseno: string,
 *   prenda: string,
 *   estado: string,
 *   grupo?: boolean,
 *   descuento?: number,
 *   props?: Record<string, string>,
 * }} Hoja
 */

/** @type {Hoja | null} */
let hoja = null;

/** @type {Promise<Producto[]> | null} */
let prendasPedidas = null;

/** "Jockey ... - Negro / Camel" -> "Negro / Camel". @param {string} titulo */
const nombreModelo = (titulo) => (titulo.includes(' - ') ? titulo.split(' - ').slice(1).join(' - ') : titulo);

/** @param {string} nombre */
const esRetrato = (nombre) => /retrato|mascota/i.test(nombre);

/** @param {unknown} valor */
function primerEntero(valor) {
  const hallado = String(valor ?? '').match(/\d+/);
  return hallado ? Number(hallado[0]) : null;
}

/** @param {string} texto */
function estilos(texto) {
  return String(texto || '')
    .split(',')
    .map((parte) => parte.trim().toLowerCase())
    .filter(Boolean);
}

/** @param {string} url */
function pedirPrendas(url) {
  if (!prendasPedidas) {
    prendasPedidas = fetch(url, { credentials: 'same-origin' })
      .then((respuesta) => {
        if (!respuesta.ok) throw new Error(ERROR_GENERICO);
        return respuesta.text();
      })
      .then((html) => {
        const nodo = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mc-carrito-prendas]');
        const lista = JSON.parse(nodo?.textContent || '[]');
        return Array.isArray(lista) ? lista.filter(Boolean) : [];
      })
      .catch((error) => {
        prendasPedidas = null;
        throw error;
      });
  }
  return prendasPedidas;
}

/**
 * Indice de la opcion "numero de retratos" que queda fija en un diseño, o -1.
 * @param {Hoja} h
 * @param {Producto} producto
 */
function opcionFija(h, producto) {
  if (h.modo !== 'diseno' || h.retratos == null) return -1;
  return producto.o.findIndex(esRetrato);
}

/**
 * Variantes que se pueden ofrecer: en un diseño, solo las del mismo numero de retratos.
 * @param {Hoja} h
 * @param {Producto} producto
 */
function candidatas(h, producto) {
  const fija = opcionFija(h, producto);
  if (fija < 0) return producto.v;
  return producto.v.filter((variante) => primerEntero(variante[3][fija]) === h.retratos);
}

/**
 * @param {Hoja} h
 * @param {Producto} producto
 */
function precioDesde(h, producto) {
  const disponibles = candidatas(h, producto).filter((variante) => variante[2]);
  return disponibles.length ? Math.min(...disponibles.map((variante) => variante[1])) : null;
}

/**
 * @param {Hoja} h
 * @param {number} precio
 */
function conDescuento(h, precio) {
  if (h.modo === 'mejora') return Math.max(0, precio - (h.descuento || 0));
  if (h.modo !== 'diseno' || h.pct <= 0) return precio;
  return Math.round((precio * (100 - h.pct)) / 100);
}

/**
 * @param {Hoja} h
 * @param {number} indice
 */
function elegirProducto(h, indice) {
  h.indice = indice;
  const producto = h.productos[indice];
  const opciones = candidatas(h, producto);
  const primera = opciones.find((variante) => variante[2]) || opciones[0];
  h.eleccion = primera ? [...primera[3]] : [];
}

/** @param {Hoja} h */
function varianteElegida(h) {
  const producto = h.productos[h.indice];
  if (!producto) return null;
  return (
    candidatas(h, producto).find((variante) => variante[3].every((valor, i) => valor === h.eleccion[i])) || null
  );
}

/**
 * Al cambiar una opcion, si la combinacion no existe se salta a la mas parecida que si exista.
 * @param {Hoja} h
 * @param {number} opcion
 * @param {string} valor
 */
function elegirOpcion(h, opcion, valor) {
  const producto = h.productos[h.indice];
  h.eleccion[opcion] = valor;
  if (varianteElegida(h)) return;
  const posibles = candidatas(h, producto).filter((variante) => variante[3][opcion] === valor);
  if (!posibles.length) return;
  const parecido = (/** @type {Variante} */ variante) =>
    variante[3].filter((v, i) => v === h.eleccion[i]).length + (variante[2] ? 0.5 : 0);
  posibles.sort((a, b) => parecido(b) - parecido(a));
  h.eleccion = [...posibles[0][3]];
}

/**
 * @param {Hoja} h
 * @param {string} [foco]  data-foco del elemento que debe quedar enfocado
 */
function pintarHoja(h, foco) {
  const contenedor = capa('hoja')?.querySelector('[data-mc-hoja]');
  if (!contenedor) return;

  const producto = h.productos[h.indice];
  const cerrar =
    '<button type="button" class="mc-carrito-capa__cerrar" data-mc-capa-cerrar aria-label="Cerrar" data-foco="cerrar">' +
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';

  let sub = '';
  if (h.modo === 'diseno') {
    const partes = [];
    if (h.estilo && h.estilo !== 'jockey' && h.estilo !== 'collar') partes.push('Estilo ' + h.estilo);
    if (h.retratos != null) partes.push(h.retratos + (h.retratos === 1 ? ' retrato' : ' retratos'));
    partes.push('misma foto');
    sub = partes.join(' · ');
  } else if (h.modo === 'mejora') {
    sub = 'Para esta prenda · elige el color';
  } else if (producto) {
    sub = producto.b ? producto.b + ' · va aparte, con su propia foto' : 'Va aparte, con su propia foto';
  }

  let html =
    '<div class="mc-carrito-hoja__cabeza"><div class="mc-carrito-hoja__titulos">' +
    `<h3 class="mc-carrito-hoja__titulo" id="mc-carrito-hoja-titulo">${limpio(h.titulo)}</h3>` +
    (sub ? `<p class="mc-carrito-hoja__sub">${limpio(sub)}</p>` : '') +
    `</div>${cerrar}</div>`;

  if (h.estado || !producto) {
    html += `<p class="mc-carrito-hoja__estado" role="status">${limpio(h.estado || 'Por ahora no hay otras prendas para este diseño.')}</p>`;
    contenedor.innerHTML = html;
    enfocar(contenedor, foco || 'cerrar');
    return;
  }

  if (h.grupo && h.productos.length > 1) {
    html += `<div class="mc-carrito-hoja__grupo"><span class="mc-carrito-hoja__rotulo">Modelo: <span>${limpio(nombreModelo(producto.t))}</span></span><div class="mc-carrito-hoja__modelos">`;
    h.productos.forEach((opcion, i) => {
      html +=
        `<button type="button" class="mc-carrito-hoja__modelo" data-hoja-prenda="${i}" data-foco="prenda-${i}" aria-pressed="${i === h.indice}" aria-label="${limpio(nombreModelo(opcion.t))}">` +
        (opcion.i ? `<img src="${limpio(opcion.i)}" alt="" width="100" height="100" loading="lazy">` : '') +
        `<span>${limpio(nombreModelo(opcion.t))}</span></button>`;
    });
    html += '</div></div>';
  } else if (h.productos.length > 1) {
    html += '<div class="mc-carrito-hoja__grupo"><span class="mc-carrito-hoja__rotulo">Prenda</span>';
    h.productos.forEach((opcion, i) => {
      const precio = precioDesde(h, opcion) ?? 0;
      const final = conDescuento(h, precio);
      const rebaja = final < precio;
      html +=
        `<button type="button" class="mc-carrito-hoja__prenda" data-hoja-prenda="${i}" data-foco="prenda-${i}" aria-pressed="${i === h.indice}">` +
        (opcion.i ? `<img class="mc-carrito-hoja__prenda-foto" src="${limpio(opcion.i)}" alt="" width="52" height="52" loading="lazy">` : '') +
        `<span class="mc-carrito-hoja__prenda-nombre">${limpio(opcion.t)}</span>` +
        '<span class="mc-carrito-hoja__prenda-precios"><span class="mc-carrito-hoja__prenda-linea">' +
        (rebaja ? `<s class="mc-carrito-hoja__antes">${dinero(precio)}</s>` : '') +
        `<span class="mc-carrito-hoja__precio">${dinero(final)}</span></span>` +
        (rebaja ? `<span class="mc-carrito-hoja__ahorras">Ahorras ${dinero(precio - final)}</span>` : '') +
        '</span></button>';
    });
    html += '</div>';
  }

  const fija = opcionFija(h, producto);
  const variantes = candidatas(h, producto);
  const unaSola = producto.o.length === 1 && producto.v.length === 1;
  if (!unaSola) {
    producto.o.forEach((nombre, i) => {
      if (i === fija) return;
      const valores = [...new Set(variantes.map((variante) => variante[3][i]))];
      if (valores.length < 1) return;
      const talla = /talla/i.test(nombre);
      html +=
        '<div class="mc-carrito-hoja__grupo">' +
        `<span class="mc-carrito-hoja__rotulo">${limpio(nombre)}` +
        (talla ? '' : `: <span>${limpio(h.eleccion[i] ?? '')}</span>`) +
        '</span><div class="mc-carrito-hoja__chips">';
      const fotoDe = (/** @type {string} */ valor) => variantes.find((variante) => variante[3][i] === valor && variante[4])?.[4] ?? '';
      const conFotos = !talla && !/color/i.test(nombre) && valores.length > 1 && valores.every((valor) => fotoDe(valor));
      if (conFotos) {
        html = html.replace(/<div class="mc-carrito-hoja__chips">$/, '<div class="mc-carrito-hoja__modelos">');
        valores.forEach((valor, n) => {
          const hay = variantes.some((variante) => variante[3][i] === valor && variante[2]);
          html +=
            `<button type="button" class="mc-carrito-hoja__modelo${hay ? '' : ' mc-carrito-hoja__chip--agotada'}" data-hoja-opcion="${i}" data-valor="${limpio(valor)}" data-foco="opcion-${i}-${n}" aria-pressed="${valor === h.eleccion[i]}">` +
            `<img src="${limpio(fotoDe(valor))}" alt="" width="100" height="100" loading="lazy"><span>${limpio(valor)}</span></button>`;
        });
        html += '</div></div>';
        return;
      }
      valores.forEach((valor, n) => {
        const hay = variantes.some((variante) => variante[3][i] === valor && variante[2]);
        const tono = !talla && /color/i.test(nombre) && producto.c ? producto.c[valor] : '';
        html +=
          `<button type="button" class="mc-carrito-hoja__chip${talla ? ' mc-carrito-hoja__chip--talla' : ''}${hay ? '' : ' mc-carrito-hoja__chip--agotada'}"` +
          ` data-hoja-opcion="${i}" data-valor="${limpio(valor)}" data-foco="opcion-${i}-${n}" aria-pressed="${valor === h.eleccion[i]}">` +
          (tono ? `<i class="mc-carrito-hoja__tono" style="background:${limpio(tono)}"></i>` : '') +
          `${limpio(valor)}</button>`;
      });
      html += '</div></div>';
    });
  }

  const elegida = varianteElegida(h);
  if (elegida && elegida[2]) {
    const texto = (h.modo === 'diseno' ? 'Agregar al diseño · ' : 'Agregar al carrito · ') + dinero(conDescuento(h, elegida[1]));
    html += `<button type="button" class="mc-carrito-hoja__agregar" data-hoja-agregar data-foco="agregar">${limpio(texto)}</button>`;
  } else {
    html += '<button type="button" class="mc-carrito-hoja__agregar" data-foco="agregar" disabled>Sin stock en esta combinación</button>';
  }

  contenedor.innerHTML = html;
  enfocar(contenedor, foco || 'cerrar');
}

/**
 * @param {Element} contenedor
 * @param {string} foco
 */
function enfocar(contenedor, foco) {
  const nodo = contenedor.querySelector(`[data-foco="${foco}"]`);
  if (nodo instanceof HTMLElement) nodo.focus({ preventScroll: true });
}

/**
 * "+ Agregar otra prenda con este diseño".
 * @param {HTMLElement} boton
 */
async function abrirHojaDeDiseno(boton) {
  const tarjeta = boton.closest('[data-mc-diseno]');
  const raiz = boton.closest('[data-mc-carrito]');
  const nodoCapa = capa('hoja');
  if (!(tarjeta instanceof HTMLElement) || !(raiz instanceof HTMLElement) || !nodoCapa) return;

  const prendas = Array.from(tarjeta.querySelectorAll('[data-mc-linea]')).filter((n) => n instanceof HTMLElement);
  const diseno = tarjeta.dataset.mcDiseno || '';

  let estilo = '';
  /** @type {number | null} */
  let retratos = null;
  let mayor = 0;
  for (const prenda of /** @type {HTMLElement[]} */ (prendas)) {
    if (!estilo) estilo = estilos(prenda.dataset.estilo || '').find((e) => !UNIVERSALES.includes(e)) || '';
    if (retratos == null && prenda.dataset.retratos) retratos = primerEntero(prenda.dataset.retratos);
    const numero = Number((prenda.dataset.prenda || '').split('-').pop());
    if (Number.isFinite(numero) && numero > mayor) mayor = numero;
  }
  mayor = Math.max(mayor, prendas.length);

  /** @type {Hoja} */
  const nueva = {
    modo: 'diseno',
    productos: [],
    indice: 0,
    eleccion: [],
    titulo: estilo === 'jockey' || estilo === 'collar' ? `Otro ${estilo}` : raiz.dataset.tituloHoja || 'Otra prenda con este diseño',
    estilo,
    retratos,
    pct: Number(boton.dataset.pct ?? raiz.dataset.pct) || 0,
    grupo: estilo === 'jockey' || estilo === 'collar',
    diseno,
    prenda: `${diseno}-${mayor + 1}`,
    estado: 'Buscando prendas…',
  };
  hoja = nueva;
  abrirCapa(nodoCapa, boton);
  pintarHoja(nueva);

  try {
    const todas = await pedirPrendas(raiz.dataset.urlPrendas || '');
    if (hoja !== nueva) return;
    nueva.productos = todas.filter((producto) => {
      const propios = estilos(producto.e);
      const sirveEstilo = !estilo || propios.includes(estilo) || propios.some((e) => UNIVERSALES.includes(e));
      return sirveEstilo && precioDesde(nueva, producto) != null;
    });
    nueva.estado = '';
    if (nueva.productos.length) elegirProducto(nueva, 0);
    pintarHoja(nueva);
  } catch {
    if (hoja !== nueva) return;
    nueva.estado = 'No pudimos cargar las prendas. Cierra e intenta de nuevo.';
    pintarHoja(nueva);
  }
}

/**
 * "Agregar +" de un producto aparte con variantes.
 * @param {HTMLElement} boton
 */
function abrirHojaAparte(boton) {
  const nodoCapa = capa('hoja');
  const datos = boton.parentElement?.querySelector('[data-mc-producto]');
  if (!nodoCapa || !datos) return;
  /** @type {Producto | null} */
  let producto = null;
  try {
    producto = JSON.parse(datos.textContent || 'null');
  } catch {
    producto = null;
  }
  if (!producto) return;
  const lista = Array.isArray(producto) ? producto.filter(Boolean) : [producto];
  if (!lista.length) return;
  const modelos = lista.length > 1;

  /** @type {Hoja} */
  const nueva = {
    modo: 'aparte',
    grupo: modelos,
    productos: lista,
    indice: 0,
    eleccion: [],
    titulo: modelos ? lista[0].t.split(' - ')[0] : lista[0].t,
    estilo: '',
    retratos: null,
    pct: 0,
    diseno: '',
    prenda: '',
    estado: '',
  };
  elegirProducto(nueva, 0);
  hoja = nueva;
  abrirCapa(nodoCapa, boton);
  pintarHoja(nueva);
}

async function agregarDesdeHoja() {
  if (!hoja) return;
  const elegida = varianteElegida(hoja);
  if (!elegida || !elegida[2]) return;
  /** @type {Record<string, string> | undefined} */
  let propiedades = hoja.modo === 'diseno' ? { _diseno: hoja.diseno, _prenda: hoja.prenda } : hoja.modo === 'mejora' ? hoja.props : undefined;
  if (hoja.modo === 'mejora' && propiedades) {
    /* Cada mejora va en su propia linea: sin esto, dos jockeys del mismo color se juntan en una linea
       de cantidad 2 y el descuento de regalo no se aplica. */
    propiedades = { ...propiedades, _extra_uid: Date.now().toString(36) };
  }
  const elegido = hoja.productos[hoja.indice];
  if (hoja.modo === 'aparte' && elegido && (elegido.o.some(esRetrato) || /jockey|collar/i.test(elegido.t))) {
    /* Un producto personalizado (con retratos) forma su propio diseño: tiene "Quitar prenda" y "Agregar otra prenda". */
    const nuevo = 'd' + Date.now().toString(36);
    propiedades = { _diseno: nuevo, _prenda: nuevo + '-1' };
  }
  /* Se cierra la hoja de inmediato: el carrito muestra el avance y, si algo falla, el aviso. */
  cerrarCapas();
  await agregar(elegida[0], propiedades);
}

/* ------------------------------------------------------------
 * Caja de regalo
 * ------------------------------------------------------------ */

function botonCaja() {
  const nodo = document.querySelector('[data-mc-caja]');
  return nodo instanceof HTMLElement ? nodo : null;
}

/** @param {Element | null} origen */
function abrirInfoCaja(origen) {
  const nodoCapa = capa('caja');
  if (!nodoCapa) return;
  const boton = nodoCapa.querySelector('[data-mc-caja-cta]');
  const puesta = botonCaja()?.getAttribute('aria-pressed') === 'true';
  if (boton instanceof HTMLElement) {
    boton.textContent = (puesta ? boton.dataset.textoLista : boton.dataset.textoAgregar) || '';
    boton.dataset.puesta = puesta ? 'si' : 'no';
  }
  abrirCapa(nodoCapa, origen);
}

/** @param {HTMLElement} boton  [data-mc-caja] */
function alternarCaja(boton) {
  if (boton.dataset.key) return actualizar({ [boton.dataset.key]: 0 });
  if (boton.dataset.variante) return agregar(boton.dataset.variante);
  return Promise.resolve(false);
}

/* ------------------------------------------------------------
 * Acciones de las lineas
 * ------------------------------------------------------------ */

/** @param {HTMLElement} boton  [data-mc-cantidad] */
function cambiarCantidad(boton) {
  const linea = boton.closest('[data-mc-linea]');
  if (!(linea instanceof HTMLElement) || !linea.dataset.key) return;
  const cantidad = Math.max(0, (Number(linea.dataset.cantidad) || 0) + (Number(boton.dataset.delta) || 0));
  if (cantidad === 0) linea.classList.add('mc-carrito__linea--saliendo');
  else {
    const numero = linea.querySelector('.mc-carrito__cantidad-numero');
    if (numero) numero.textContent = String(cantidad);
  }
  actualizar({ [linea.dataset.key]: cantidad });
}

/** Quita la prenda y, con ella, sus mejoras. @param {HTMLElement} boton */
function quitarPrenda(boton) {
  const linea = boton.closest('[data-mc-linea]');
  if (!(linea instanceof HTMLElement) || !linea.dataset.key) return;
  /* Se oculta al instante; el carrito real llega despues. */
  linea.classList.add('mc-carrito__linea--saliendo');
  /** @type {Record<string, number>} */
  const cambios = { [linea.dataset.key]: 0 };
  for (const mejora of linea.querySelectorAll('[data-mc-mejora][data-key]')) {
    if (mejora instanceof HTMLElement && mejora.dataset.key) cambios[mejora.dataset.key] = 0;
  }
  actualizar(cambios);
}

/**
 * Mejora con colores (por ejemplo el jockey): se elige el color en la hoja antes de agregarla.
 * @param {HTMLElement} boton  [data-mc-mejora-elegir]
 */
function abrirHojaDeMejora(boton) {
  const nodoCapa = capa('hoja');
  const linea = boton.closest('[data-mc-linea]');
  const datos = boton.nextElementSibling;
  if (!nodoCapa || !(linea instanceof HTMLElement) || !linea.dataset.prenda || !datos) return;
  /** @type {Producto | null} */
  let producto = null;
  try {
    producto = JSON.parse(datos.textContent || 'null');
  } catch {
    producto = null;
  }
  if (!producto || Array.isArray(producto)) return;
  /** @type {Record<string, string>} */
  const props = { _mejora_de: linea.dataset.prenda };
  if (linea.dataset.diseno) props._diseno = linea.dataset.diseno;
  if (Number(boton.dataset.descuento) > 0) props._mc_extra_discount_clp = String(Number(boton.dataset.descuento));
  if (Number(boton.dataset.extraId) > 0) props.__mc_extra_id = String(boton.dataset.extraId);

  /** @type {Hoja} */
  const nueva = {
    modo: 'mejora',
    productos: [producto],
    indice: 0,
    eleccion: [],
    titulo: producto.t,
    estilo: '',
    retratos: null,
    pct: 0,
    diseno: '',
    prenda: '',
    estado: '',
    descuento: (Number(boton.dataset.descuento) || 0) * 100,
    props,
  };
  elegirProducto(nueva, 0);
  hoja = nueva;
  abrirCapa(nodoCapa, boton);
  pintarHoja(nueva);
}

/** @param {HTMLElement} boton  [data-mc-mejora] */
function alternarMejora(boton) {
  if (!boton.dataset.key && boton.hasAttribute('data-mc-mejora-elegir')) return abrirHojaDeMejora(boton);
  /* La casilla cambia al tocarla; si algo falla, el carrito se repinta como estaba. */
  boton.classList.add('mc-carrito__mejora--cargando');
  boton.setAttribute('aria-pressed', String(!boton.dataset.key));
  if (boton.dataset.key) {
    actualizar({ [boton.dataset.key]: 0 });
    return;
  }
  const linea = boton.closest('[data-mc-linea]');
  if (!(linea instanceof HTMLElement) || !boton.dataset.variante || !linea.dataset.prenda) return;
  /** @type {Record<string, string>} */
  const propiedades = { _mejora_de: linea.dataset.prenda };
  if (linea.dataset.diseno) propiedades._diseno = linea.dataset.diseno;
  if (Number(boton.dataset.descuento) > 0) propiedades._mc_extra_discount_clp = String(Number(boton.dataset.descuento));
  if (Number(boton.dataset.extraId) > 0) propiedades.__mc_extra_id = String(boton.dataset.extraId);
  agregar(boton.dataset.variante, propiedades);
}

/* ------------------------------------------------------------
 * Escucha unica
 * ------------------------------------------------------------ */

document.addEventListener('click', (evento) => {
  const origen = evento.target instanceof Element ? evento.target : null;
  if (!origen) return;

  /* Capas primero: quedan encima del carrito. */
  const abierta = capaAbierta();
  if (abierta && abierta.contains(origen)) {
    if (origen === abierta || origen.closest('[data-mc-capa-cerrar]')) {
      cerrarCapas();
      return;
    }
    const prenda = origen.closest('[data-hoja-prenda]');
    if (prenda instanceof HTMLElement && hoja) {
      elegirProducto(hoja, Number(prenda.dataset.hojaPrenda) || 0);
      pintarHoja(hoja, prenda.dataset.foco);
      return;
    }
    const opcion = origen.closest('[data-hoja-opcion]');
    if (opcion instanceof HTMLElement && hoja) {
      elegirOpcion(hoja, Number(opcion.dataset.hojaOpcion) || 0, opcion.dataset.valor || '');
      pintarHoja(hoja, opcion.dataset.foco);
      return;
    }
    if (origen.closest('[data-hoja-agregar]')) {
      agregarDesdeHoja();
      return;
    }
    const cta = origen.closest('[data-mc-caja-cta]');
    if (cta instanceof HTMLElement) {
      const boton = botonCaja();
      if (cta.dataset.puesta === 'si' || !boton) {
        cerrarCapas();
      } else {
        alternarCaja(boton).then((hecho) => {
          if (hecho) cerrarCapas();
        });
      }
    }
    return;
  }

  if (!origen.closest('[data-mc-carrito]')) return;

  const flecha = origen.closest('[data-mc-reco-prev], [data-mc-reco-next]');
  if (flecha) {
    const lista = flecha.closest('.mc-carrito__completa')?.querySelector('.mc-carrito__recos');
    if (lista instanceof HTMLElement) {
      const paso = (lista.firstElementChild?.getBoundingClientRect().width ?? 290) + 10;
      lista.scrollBy({ left: flecha.hasAttribute('data-mc-reco-next') ? paso : -paso, behavior: 'smooth' });
    }
    return;
  }

  const accion = origen.closest(
    '[data-mc-cantidad], [data-mc-quitar], [data-mc-mejora], [data-mc-otra], [data-mc-agregar], [data-mc-elegir], [data-mc-caja], [data-mc-caja-info]'
  );
  if (!(accion instanceof HTMLElement)) return;

  if (accion.hasAttribute('data-mc-caja-info')) return abrirInfoCaja(accion);
  if (accion.hasAttribute('data-mc-otra')) return void abrirHojaDeDiseno(accion);
  if (accion.hasAttribute('data-mc-elegir')) return abrirHojaAparte(accion);
  if (ocupado) return;
  if (accion.hasAttribute('data-mc-cantidad')) return cambiarCantidad(accion);
  if (accion.hasAttribute('data-mc-quitar')) return quitarPrenda(accion);
  if (accion.hasAttribute('data-mc-mejora')) return alternarMejora(accion);
  if (accion.hasAttribute('data-mc-caja')) return void alternarCaja(accion);
  if (accion.hasAttribute('data-mc-agregar') && accion.dataset.variante) return void agregar(accion.dataset.variante);
});

/* Escape cierra primero la capa abierta, no el panel entero. */
document.addEventListener(
  'keydown',
  (evento) => {
    if (evento.key !== 'Escape' || !capaAbierta()) return;
    evento.preventDefault();
    evento.stopPropagation();
    cerrarCapas();
  },
  true
);

/* Si el panel se cierra con una capa abierta, la capa no debe seguir ahi al volver. */
document.addEventListener('theme-drawer:close', () => {
  if (capaAbierta()) cerrarCapas();
});

/*
 * En celular la pagina se fija mientras el carrito esta abierto (ver mc-carrito.css) y eso la manda
 * arriba. Se guarda donde estaba y se devuelve a ese punto al cerrar.
 */
{
  const panel = document.getElementById('cart-drawer');
  if (panel) {
    let posicion = window.scrollY;
    let sinGuardarHasta = 0;
    window.addEventListener(
      'scroll',
      () => {
        /* Con la pagina fijada, o recien cerrado el carrito, la posicion leida seria 0: no se guarda. */
        if (panel.hasAttribute('open') || document.documentElement.hasAttribute('scroll-lock') || Date.now() < sinGuardarHasta) return;
        posicion = window.scrollY;
      },
      { passive: true }
    );
    let estabaAbierto = panel.hasAttribute('open');
    new MutationObserver(() => {
      const abierto = panel.hasAttribute('open');
      if (estabaAbierto && !abierto) {
        sinGuardarHasta = Date.now() + 1500;
        /* El tema tambien mueve el scroll al cerrar: se insiste unos instantes hasta que la pagina quede donde estaba. */
        let intentos = 0;
        const devolver = () => {
          if (document.documentElement.hasAttribute('scroll-lock') || Math.abs(window.scrollY - posicion) > 2) {
            window.scrollTo({ top: posicion, behavior: 'instant' });
          }
          if (++intentos < 12) setTimeout(devolver, 50);
        };
        requestAnimationFrame(devolver);
      }
      estabaAbierto = abierto;
    }).observe(panel, { attributes: true, attributeFilter: ['open'] });
  }
}
